import { extractSongTitle } from '../core/parser';
import { getRequiredElement } from './dom';
import { triggerHaptic } from './haptics';
import { showToast } from './toast';

const COPY_BTN_DEFAULT_HTML = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> <span id="copyBtnText">Copy</span>`;

let copyFlashTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Triggers a visual flash and icon checkmark on the copy button.
 */
export function flashCopySuccess(): void {
  const copyBtn = getRequiredElement<HTMLButtonElement>('copyBtn');
  const dockCopyBtn = document.getElementById('dockCopyBtn') as HTMLButtonElement | null;
  const dockCopyText = document.getElementById('dockCopyText');

  if (copyFlashTimeout) {
    clearTimeout(copyFlashTimeout);
  }

  triggerHaptic(15);
  copyBtn.classList.add('success-flash');
  copyBtn.innerHTML = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg> <span>Copied!</span>`;

  if (dockCopyBtn && dockCopyText) {
    dockCopyBtn.classList.add('success-flash');
    dockCopyText.textContent = 'Copied!';
  }

  showToast('✓ Copied to clipboard!');

  copyFlashTimeout = setTimeout(() => {
    copyBtn.classList.remove('success-flash');
    copyBtn.innerHTML = COPY_BTN_DEFAULT_HTML;
    if (dockCopyBtn && dockCopyText) {
      dockCopyBtn.classList.remove('success-flash');
      dockCopyText.textContent = 'Copy';
    }
  }, 2200);
}

/**
 * Reads text from the device clipboard with single-tap friction reduction for mobile devices.
 */
export async function pasteFromClipboard(): Promise<string | null> {
  if (!navigator.clipboard || typeof navigator.clipboard.readText !== 'function') {
    showToast('Clipboard access not supported. Please paste manually.');
    return null;
  }

  try {
    const text = await navigator.clipboard.readText();
    if (!text || !text.trim()) {
      showToast('Clipboard is empty.');
      return null;
    }

    triggerHaptic(12);
    return text;
  } catch (err) {
    console.warn('Clipboard read error (permission may be required):', err);
    showToast('Could not read clipboard. Please tap inside and paste manually.');
    return null;
  }
}

/**
 * High-resilience copy function designed to work reliably across desktop, iOS Safari,
 * and Android web views using a three-tier fallback mechanism.
 */
import { getEditorValue } from './editor';

/**
 * Returns current lyrics text from the active editor or fallback DOM.
 */
function getActiveLyricsText(): string {
  const val = getEditorValue();
  if (val) return val;
  const el = document.getElementById('input') as HTMLTextAreaElement | null;
  return el ? el.value : '';
}

/**
 * High-resilience copy function designed to work reliably across desktop, iOS Safari,
 * and Android web views using a multi-tier fallback mechanism.
 */
export async function copyOutput(): Promise<void> {
  const text = getActiveLyricsText();

  if (!text.trim()) {
    showToast('Nothing to copy yet. Paste or enter lyrics first.');
    return;
  }

  let copied = false;

  // 1. Try modern Async Clipboard API (Works on HTTPS and secure contexts)
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text);
      copied = true;
    } catch (err) {
      console.warn('Async Clipboard API failed (common in insecure or restricted web views):', err);
    }
  }

  // 2. Offscreen textarea fallback (specifically for environments without Clipboard API)
  if (!copied) {
    try {
      const tempArea = document.createElement('textarea');
      tempArea.value = text;
      tempArea.style.position = 'fixed';
      tempArea.style.top = '0';
      tempArea.style.left = '-9999px';
      tempArea.style.width = '2em';
      tempArea.style.height = '2em';
      tempArea.style.padding = '0';
      tempArea.style.border = 'none';
      tempArea.style.outline = 'none';
      tempArea.style.boxShadow = 'none';
      tempArea.style.background = 'transparent';
      document.body.appendChild(tempArea);
      tempArea.focus();
      tempArea.select();
      tempArea.setSelectionRange(0, text.length);
      copied = document.execCommand('copy');
      document.body.removeChild(tempArea);
    } catch (err) {
      console.error('Fallback execCommand failed:', err);
    }
  }

  if (copied) {
    if (window.getSelection) {
      window.getSelection()?.removeAllRanges();
    }
    flashCopySuccess();
  } else {
    showToast('Could not copy automatically. Please copy manually.');
  }
}

/**
 * Triggers native Web Share API dialog on mobile and supported desktop devices.
 * Shares lyrics text and title in a single atomic user gesture without file permission errors.
 */
export async function shareOutput(): Promise<void> {
  const text = getActiveLyricsText();

  if (!text || !text.trim()) {
    showToast('Nothing to share yet. Paste or enter lyrics first.');
    return;
  }

  if (typeof navigator.share !== 'function') {
    await copyOutput();
    showToast('Web Share not available on this device. Copied to clipboard!');
    return;
  }

  try {
    const title = extractSongTitle(text);
    await navigator.share({
      title: title ? `${title} (Lyrics)` : 'Converted LRC Lyrics',
      text: text
    });
    triggerHaptic(15);
    showToast('✓ Shared successfully!');
  } catch (err) {
    const error = err as Error;
    // User intentionally closed or dismissed the share sheet
    if (error.name === 'AbortError') {
      return;
    }
    console.warn('Share error:', err);
    try {
      await copyOutput();
      showToast('Share unavailable. Copied to clipboard instead!');
    } catch {
      showToast('Share not completed.');
    }
  }
}

export const copyLyrics = copyOutput;
export const shareLyrics = shareOutput;
